/**
 * The shop's own account with SmartSpaza.
 *
 * Deliberately not `adminRepository`. That one talks to `/api/v1/admin/*` and is
 * about every merchant on the platform; this one talks to `/api/v1/billing/*`
 * and is only ever about the caller's own — the server resolves which merchant
 * from the session, and nothing here sends an id to say.
 *
 * Money arrives as decimal strings and stays that way until it is formatted.
 * Parsing a rand amount into a float to hold it in state is how a total ends up
 * a cent out on a page about what somebody owes.
 */

import { api } from "@/api/client";

import type { Page } from "./adminRepository";

// ═══════════════════════════════════════════════════════════════════════════════
// Shapes
// ═══════════════════════════════════════════════════════════════════════════════
export type SubscriptionStatus =
  | "TRIAL"
  | "ACTIVE"
  | "PAST_DUE"
  | "SUSPENDED"
  | "CANCELLED"
  | "EXPIRED";

export type InvoiceStatus =
  | "ISSUED"
  | "PARTIALLY_PAID"
  | "PAID"
  | "OVERDUE"
  | "VOID";

export type ChangeKind = "UPGRADE" | "DOWNGRADE" | "LATERAL";

export interface MySubscription {
  id: string;
  package: string;
  package_code: string;
  package_description: string;
  billing_cycle: "MONTHLY" | "ANNUAL";
  status: SubscriptionStatus;
  price: string;
  currency: string;
  start_date: string;
  trial_end_date: string | null;
  current_period_start: string;
  current_period_end: string;
  cancel_at_period_end: boolean;
  cancelled_at: string | null;
}

/** One meter: "Tills 2 of 3". `limit` null means unlimited. */
export interface UsageLine {
  key: string;
  label: string;
  used: number;
  limit: number | null;
  remaining: number | null;
  percent: number | null;
  is_unlimited: boolean;
  is_exhausted: boolean;
}

export interface PackageRequest {
  id: string;
  requested_package: string;
  requested_package_code: string;
  change_kind: ChangeKind;
  status: "PENDING" | "ACTIONED" | "DECLINED" | "WITHDRAWN";
  note: string;
  requested_by: string;
  created_at: string;
  resolved_at: string | null;
}

/**
 * The whole Billing screen in one response.
 *
 * One request rather than four: the page is useless in pieces, and four round
 * trips on a shop's mobile connection are four chances to show a spinner.
 */
export interface BillingOverview {
  /** Null while a merchant is being provisioned — not an error. */
  subscription: MySubscription | null;
  usage: UsageLine[];
  outstanding: string;
  open_invoice_count: number;
  pending_request: PackageRequest | null;
}

export interface PackageOffer {
  id: string;
  code: string;
  name: string;
  description: string;
  monthly_price: string;
  annual_price: string | null;
  currency: string;
  trial_days: number;
  display_order: number;
  features: string[];
  limits: Record<string, number | null>;
  is_current: boolean;
}

export interface MyInvoice {
  id: string;
  number: string;
  status: InvoiceStatus;
  issue_date: string | null;
  due_date: string | null;
  period_start: string | null;
  period_end: string | null;
  currency: string;
  total: string;
  amount_paid: string;
  balance: string;
}

export interface MyInvoiceItem {
  id: string;
  description: string;
  quantity: string;
  unit_price: string;
  subtotal: string;
  tax_rate: string;
  tax_amount: string;
  total: string;
}

export interface MyInvoiceDetail extends MyInvoice {
  subtotal: string;
  discount_amount: string;
  tax_amount: string;
  tax_mode_snapshot: string;
  tax_rate_snapshot: string;
  notes: string;
  voided_at: string | null;
  items: MyInvoiceItem[];
}

export interface MyPayment {
  id: string;
  reference: string;
  amount: string;
  currency: string;
  payment_date: string;
  method: string;
  status: string;
  external_reference: string;
  reversed_at: string | null;
}

// ═══════════════════════════════════════════════════════════════════════════════
// Calls
// ═══════════════════════════════════════════════════════════════════════════════
export const billingRepository = {
  overview: () => api.get<BillingOverview>("/billing/subscription/"),

  packages: () => api.get<PackageOffer[]>("/billing/packages/"),

  invoices: (params: { status?: string; page?: number } = {}) => {
    const query = new URLSearchParams();
    if (params.status) query.set("status", params.status);
    if (params.page) query.set("page", String(params.page));

    const suffix = query.toString();
    return api.get<Page<MyInvoice>>(`/billing/invoices/${suffix ? `?${suffix}` : ""}`);
  },

  invoice: (id: string) => api.get<MyInvoiceDetail>(`/billing/invoices/${id}/`),

  payments: () => api.get<Page<MyPayment>>("/billing/payments/"),

  /** Ask to be moved. Nothing changes until SmartSpaza acts on it. */
  requestPackage: (packageCode: string, note: string) =>
    api.post<PackageRequest>("/billing/package-request/", {
      package_code: packageCode,
      note,
    }),

  withdrawRequest: () => api.delete<void>("/billing/package-request/"),
};
